import React, { useState, useEffect } from "react";
import {
  Layout,
  Input,
  Button,
  Table,
  Typography,
  Space,
  message,
  Tag,
  Select,
  ConfigProvider,
  theme,
  Switch,
  Card,
} from "antd";
import {
  SearchOutlined,
  DownloadOutlined,
  LinkOutlined,
  FileImageOutlined,
  PlaySquareOutlined,
  SoundOutlined,
  FileOutlined,
  MoonOutlined,
  SunOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import axios from "axios";
import type { ColumnsType } from "antd/es/table";
import "./App.css";

const { Header, Content } = Layout;
const { Title, Text } = Typography;

type Language = "en" | "vi";

interface DownloadableLink {
  key: string;
  url: string;
  type: "Image" | "Video" | "Audio" | "File";
}

const translations = {
  en: {
    title: "SOFT IDM",
    heroTitle: "Extract Media Effortlessly",
    description:
      "Paste any URL below to instantly crawl and download files, images, audio, and videos. Our systems will handle the rest.",
    placeholder: "https://example.com/page-with-media",
    fetchBtn: "Extract Now",
    assetType: "Asset Type",
    url: "Source URL",
    action: "Action",
    downloadBtn: "Download",
    errEmpty: "Please enter a valid URL",
    errCrawl: "Failed to crawl the provided URL. Ensure it is accessible.",
    successMsg: (count: number) =>
      `Extraction complete. Found ${count} downloadable assets.`,
    types: {
      Image: "Image",
      Video: "Video",
      Audio: "Audio",
      File: "File",
    },
  },
  vi: {
    title: "SOFT IDM",
    heroTitle: "Trích Xuất Dữ Liệu Nhanh Chóng",
    description:
      "Dán bất kỳ URL nào vào bên dưới để thu thập và tải xuống tệp, hình ảnh, âm thanh và video. Hệ thống của chúng tôi sẽ xử lý phần còn lại.",
    placeholder: "https://vi-du.vn/trang-co-media",
    fetchBtn: "Trích Xuất Ngay",
    assetType: "Loại Tệp",
    url: "Đường Dẫn",
    action: "Thao Tác",
    downloadBtn: "Tải Về",
    errEmpty: "Vui lòng nhập một URL hợp lệ",
    errCrawl: "Thu thập dữ liệu thất bại. Đảm bảo URL có thể truy cập được.",
    successMsg: (count: number) =>
      `Hoàn tất! Đã tìm thấy ${count} tệp có thể tải xuống.`,
    types: {
      Image: "Hình ảnh",
      Video: "Video",
      Audio: "Âm thanh",
      File: "Tệp tin",
    },
  },
};

const App: React.FC = () => {
  const [lang, setLang] = useState<Language>(() => {
    const savedLang = localStorage.getItem("idm_lang");
    return savedLang === "vi" || savedLang === "en" ? savedLang : "en";
  });

  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    return localStorage.getItem("idm_theme") === "dark";
  });

  const [targetUrl, setTargetUrl] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [links, setLinks] = useState<DownloadableLink[]>([]);

  const t = translations[lang];

  useEffect(() => {
    localStorage.setItem("idm_lang", lang);
  }, [lang]);

  useEffect(() => {
    const themeMode = isDarkMode ? "dark" : "light";
    document.documentElement.setAttribute("data-theme", themeMode);
    localStorage.setItem("idm_theme", themeMode);
  }, [isDarkMode]);

  const handleCrawl = async () => {
    if (!targetUrl) {
      message.error(t.errEmpty);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `https://okms.soft.io.vn/idm/crawl.php?url=${encodeURIComponent(targetUrl)}`,
      );

      const formattedData = response.data.map((item: any, index: number) => ({
        key: String(index),
        url: item.url,
        type: item.type,
      }));

      setLinks(formattedData);
      message.success(t.successMsg(formattedData.length));
    } catch (error) {
      message.error(t.errCrawl);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (url: string) => {
    // Pointing to the Netlify Serverless Function
    const proxyUrl = `https://okms.soft.io.vn/idm/download.php?url=${encodeURIComponent(url)}`;

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = proxyUrl;

    document.body.appendChild(iframe);

    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 5000);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "Image":
        return <FileImageOutlined />;
      case "Video":
        return <PlaySquareOutlined />;
      case "Audio":
        return <SoundOutlined />;
      default:
        return <FileOutlined />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "Image":
        return "blue";
      case "Video":
        return "geekblue";
      case "Audio":
        return "cyan";
      default:
        return "default";
    }
  };

  const columns: ColumnsType<DownloadableLink> = [
    {
      title: t.assetType,
      dataIndex: "type",
      key: "type",
      width: 160,
      render: (text: "Image" | "Video" | "Audio" | "File") => (
        <Tag
          color={getTypeColor(text)}
          icon={getTypeIcon(text)}
          style={{
            padding: "4px 12px",
            borderRadius: "6px",
            fontSize: "13px",
            border: "1px solid transparent",
            fontWeight: 500,
          }}
        >
          {t.types[text] || text}
        </Tag>
      ),
    },
    {
      title: t.url,
      dataIndex: "url",
      key: "url",
      ellipsis: true,
      render: (text) => (
        <Text
          copyable={{ text }} /* <-- explicitly set the text to be copied */
          ellipsis
          style={{
            width: "100%",
            maxWidth: 500,
            color: isDarkMode ? "#94a3b8" : "#64748b",
            fontWeight: 500,
          }}
        >
          <LinkOutlined style={{ marginRight: 8 }} />
          {text}
        </Text>
      ),
    },
    {
      title: t.action,
      key: "action",
      width: 150,
      align: "center",
      render: (_, record) => (
        <Button
          type="primary"
          shape="round"
          icon={<DownloadOutlined />}
          onClick={() => handleDownload(record.url)}
          style={{ fontWeight: 600, boxShadow: "none" }}
        >
          {t.downloadBtn}
        </Button>
      ),
    },
  ];

  // Updated Blueish / Professional Palette
  const currentLayoutBg = isDarkMode ? "#0f172a" : "#f1f5f9";
  const currentHeaderBg = isDarkMode ? "#1e293b" : "#ffffff";
  const currentTextColor = isDarkMode ? "#f8fafc" : "#0f172a";
  const subTextColor = isDarkMode ? "#94a3b8" : "#475569";

  return (
    <ConfigProvider
      theme={{
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          fontFamily: "'Inter', sans-serif",
          colorPrimary: "#2563eb", // Bold professional blue
          borderRadius: 8, // Sharper borders for a structural feel
          colorText: currentTextColor,
          colorTextHeading: currentTextColor,
          colorBgContainer: currentHeaderBg,
          colorBgElevated: currentHeaderBg,
        },
      }}
    >
      <Layout
        style={{
          minHeight: "100vh",
          backgroundColor: currentLayoutBg,
        }}
      >
        <Header
          style={{
            backgroundColor: currentHeaderBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 12px",
            height: "72px",
            borderBottom: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <ThunderboltOutlined
              style={{ fontSize: "24px", color: "#2563eb" }}
            />
            <Title
              level={3}
              className="gradient-text"
              style={{ margin: 0, letterSpacing: "-0.5px", fontWeight: 800 }}
            >
              {t.title}
            </Title>
          </div>

          <Space size="middle">
            <Select
              value={lang}
              onChange={(value) => setLang(value as Language)}
              style={{ width: 140 }}
              bordered={false}
              options={[
                {
                  value: "en",
                  label: (
                    <span
                      style={{
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ marginRight: 8, fontSize: "16px" }}>
                        🇺🇸
                      </span>{" "}
                      English
                    </span>
                  ),
                },
                {
                  value: "vi",
                  label: (
                    <span
                      style={{
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ marginRight: 8, fontSize: "16px" }}>
                        🇻🇳
                      </span>{" "}
                      Tiếng Việt
                    </span>
                  ),
                },
              ]}
            />
            <Switch
              checkedChildren={<MoonOutlined />}
              unCheckedChildren={<SunOutlined />}
              checked={isDarkMode}
              onChange={(checked) => setIsDarkMode(checked)}
              style={{ backgroundColor: isDarkMode ? "#3b82f6" : undefined }}
            />
          </Space>
        </Header>

        <Content
          style={{
            padding: "60px 24px",
            maxWidth: "960px",
            margin: "0 auto",
            width: "100%",
          }}
        >
          <div className="hero-section">
            <Title
              level={1}
              style={{
                fontWeight: 800,
                marginBottom: "16px",
                letterSpacing: "-1.5px",
                fontSize: "42px",
              }}
            >
              {t.heroTitle}
            </Title>
            <Text
              style={{
                fontSize: "18px",
                maxWidth: "600px",
                display: "inline-block",
                color: subTextColor,
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              {t.description}
            </Text>
          </div>

          <Space
            direction="vertical"
            size="large"
            style={{ display: "flex", marginTop: "32px" }}
          >
            <Card className="custom-card" bodyStyle={{ padding: "32px" }}>
              <Space.Compact style={{ width: "100%" }} size="large">
                <Input
                  autoFocus
                  size="large"
                  placeholder={t.placeholder}
                  value={targetUrl}
                  onChange={(e) => setTargetUrl(e.target.value)}
                  onPressEnter={handleCrawl}
                  prefix={
                    <LinkOutlined
                      style={{
                        color: isDarkMode ? "#64748b" : "#94a3b8",
                        marginRight: "12px",
                        fontSize: "18px",
                      }}
                    />
                  }
                  style={{
                    padding: "16px 24px",
                    fontSize: "16px",
                    borderTopLeftRadius: "8px",
                    borderBottomLeftRadius: "8px",
                  }}
                />
                <Button
                  type="primary"
                  size="large"
                  icon={<SearchOutlined />}
                  loading={loading}
                  onClick={handleCrawl}
                  style={{
                    padding: "0 12px",
                    fontSize: "16px",
                    height: "auto",
                    fontWeight: 600,
                    borderTopRightRadius: "8px",
                    borderBottomRightRadius: "8px",
                    boxShadow: "none",
                  }}
                >
                  {t.fetchBtn}
                </Button>
              </Space.Compact>
            </Card>

            {links.length > 0 && (
              <Card className="custom-card" bodyStyle={{ padding: "24px" }}>
                <Table
                  columns={columns}
                  dataSource={links}
                  pagination={{ pageSize: 8, position: ["bottomCenter"] }}
                  rowKey="key"
                  scroll={{ x: 600 }}
                />
              </Card>
            )}
          </Space>
        </Content>
      </Layout>
    </ConfigProvider>
  );
};

export default App;
